(() => {
  const EMBLA_CDN = 'https://unpkg.com/embla-carousel/embla-carousel.umd.js';
  const sliderRegistry = new WeakMap();

  const ensureEmbla = (() => {
    let loaderPromise;
    return () => {
      if (typeof window !== 'undefined' && window.EmblaCarousel) {
        return Promise.resolve(window.EmblaCarousel);
      }

      if (!loaderPromise) {
        loaderPromise = new Promise((resolve, reject) => {
          if (typeof document === 'undefined') {
            reject(new Error('Embla requires a document to load.'));
            return;
          }

          const existingScript = document.querySelector('script[data-embla-src="true"]');
          if (existingScript) {
            existingScript.addEventListener('load', () => resolve(window.EmblaCarousel));
            existingScript.addEventListener('error', reject);
            return;
          }

          const script = document.createElement('script');
          script.src = EMBLA_CDN;
          script.async = true;
          script.defer = true;
          script.dataset.emblaSrc = 'true';
          script.onload = () => resolve(window.EmblaCarousel);
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      return loaderPromise;
    };
  })();

  const formatMoney = (cents, format) => {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }
    const value = (Number(cents) / 100).toFixed(2);
    return `$${value}`;
  };

  const setupSlider = async (section) => {
    const slider = section.querySelector('[data-hero-slider]');
    const viewport = slider?.querySelector('[data-hero-viewport]');

    if (!slider || !viewport) {
      return null;
    }

    try {
      await ensureEmbla();
    } catch (error) {
      console.error('Failed to load EmblaCarousel', error);
      return null;
    }

    if (typeof EmblaCarousel !== 'function') {
      console.error('EmblaCarousel is not available on window.');
      return null;
    }

    const prev = slider.querySelector('[data-hero-prev]');
    const next = slider.querySelector('[data-hero-next]');
    const thumbnails = Array.from(section.querySelectorAll('[data-thumbnail-index]'));
    const initial = Number(slider.dataset.initialSlide || 0);

    const embla = EmblaCarousel(viewport, {
      loop: false,
      startIndex: Math.max(0, initial),
    });

    const slides = embla.slideNodes();

    const toggleSlideMedia = (slide, isActive) => {
      if (!(slide instanceof HTMLElement)) return;

      const inlineVideos = slide.querySelectorAll('video[data-hero-video]');
      inlineVideos.forEach((video) => {
        if (!(video instanceof HTMLMediaElement)) return;
        if (isActive) return;
        try {
          video.pause();
          video.currentTime = 0;
        } catch (error) {
          console.warn('Unable to pause hero slider video', error);
        }
      });

      const externalVideos = slide.querySelectorAll('[data-hero-external-video]');
      externalVideos.forEach((frame) => {
        if (!(frame instanceof HTMLIFrameElement) || isActive) return;
        const src = frame.getAttribute('src');
        if (!src) return;

        try {
          const videoUrl = new URL(src, window.location.origin);
          const host = videoUrl.hostname;

          if (host.includes('youtube.com') || host.includes('youtu.be')) {
            frame.contentWindow?.postMessage(
              JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
              '*'
            );
          } else if (host.includes('vimeo.com')) {
            frame.contentWindow?.postMessage({ method: 'pause' }, '*');
          }
        } catch (error) {
          console.warn('Unable to parse external video URL', error);
        }
      });
    };

    const prepareExternalVideos = () => {
      slider
        .querySelectorAll('[data-hero-external-video]')
        .forEach((frame) => {
          if (!(frame instanceof HTMLIFrameElement)) return;
          if (frame.dataset.heroExternalVideoPrepared === 'true') return;

          const src = frame.getAttribute('src');
          if (!src) return;

          try {
            const url = new URL(src, window.location.origin);
            const host = url.hostname;

            if (host.includes('youtube.com') || host.includes('youtu.be')) {
              if (!url.searchParams.has('enablejsapi')) {
                url.searchParams.set('enablejsapi', '1');
                frame.src = url.toString();
              }
            } else if (host.includes('vimeo.com')) {
              if (!url.searchParams.has('api')) {
                url.searchParams.set('api', '1');
              }
              if (!url.searchParams.has('player_id')) {
                const sectionId =
                  section?.dataset?.sectionId || section?.id || Date.now().toString();
                url.searchParams.set('player_id', `hero-slider-${sectionId}`);
              }
              frame.src = url.toString();
            }
            frame.dataset.heroExternalVideoPrepared = 'true';
          } catch (error) {
            console.warn('Unable to prepare external video', error);
          }
        });
    };

    prepareExternalVideos();

    const updateActiveState = () => {
      const selectedIndex = embla.selectedScrollSnap();
      slider.dataset.activeIndex = selectedIndex;

      slides.forEach((slide, index) => {
        const isActive = index === selectedIndex;
        slide.classList.toggle('is-active', isActive);
        toggleSlideMedia(slide, isActive);
      });

      thumbnails.forEach((thumb, index) => {
        const isActive = index === selectedIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', isActive.toString());
      });
    };

    updateActiveState();
    embla.on('select', updateActiveState);

    prev?.addEventListener('click', () => embla.scrollPrev());
    next?.addEventListener('click', () => embla.scrollNext());
    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => embla.scrollTo(index));
    });

    return {
      setByMediaId(mediaId) {
        if (!mediaId) return;
        const targetIndex = slides.findIndex(
          (slide) => Number(slide.dataset.mediaId) === Number(mediaId)
        );
        if (targetIndex >= 0) {
          embla.scrollTo(targetIndex);
        }
      },
      destroy() {
        embla.destroy();
        slides.forEach((slide) => toggleSlideMedia(slide, false));
      },
    };
  };

  const setupServingToggle = (section) => {
    section.querySelectorAll('[data-serving-toggle]').forEach((button) => {
      const panel = button.nextElementSibling;
      if (!(panel instanceof HTMLElement)) return;
      button.addEventListener('click', () => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', (!isExpanded).toString());
        panel.hidden = isExpanded;
      });
    });
  };

  const updateVariantButtons = (buttons, variantId) => {
    buttons.forEach((button) => {
      const isActive = Number(button.dataset.variantId) === Number(variantId);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-checked', isActive.toString());
    });
  };

  const updatePurchaseUI = (section, variant, config, sliderControls) => {
    const priceEl = section.querySelector('[data-price]');
    const compareEl = section.querySelector('[data-compare-price]');
    const discountEl = section.querySelector('[data-discount-badge]');
    const addToCart = section.querySelector('[data-add-to-cart-button]');
    const addToCartText = section.querySelector('[data-add-to-cart-text]');
    const variantInput = section.querySelector('[data-variant-input]');
    const translations = config.translations || {};
    const soldOutText = translations.sold_out || 'Sold out';
    const offText = translations.off || 'Off';

    if (variantInput) {
      variantInput.value = variant.id;
    }

    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price, config.moneyFormat);
    }

    if (compareEl) {
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareEl.classList.toggle('is-hidden', !hasCompare);
      compareEl.textContent = hasCompare
        ? formatMoney(variant.compare_at_price, config.moneyFormat)
        : '';
      if (discountEl) {
        if (hasCompare) {
          const discount = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          discountEl.innerHTML = `<span>${discount}% ${offText}</span>`;
        } else {
          discountEl.innerHTML = '';
        }
      }
    }

    if (addToCart) {
      addToCart.disabled = !variant.available;
    }

    if (addToCartText) {
      const defaultText = addToCartText.dataset.defaultText || 'Add to Cart';
      addToCartText.textContent = variant.available ? defaultText : soldOutText;
    }

    sliderControls?.setByMediaId?.(variant.featured_media_id || config.activeMediaId);
  };

  const parseConfig = (section) => {
    const configEl = section.querySelector('script[data-product-hero-config]');
    if (!configEl) return null;

    try {
      return JSON.parse(configEl.textContent || '{}');
    } catch (error) {
      console.error('Product hero config parse error', error);
      return null;
    }
  };

  const initializeHero = async (section) => {
    if (!section || section.dataset.productHeroInitialized === 'true') return;

    const config = parseConfig(section);
    if (!config) return;

    const variants = Array.isArray(config.variants) ? config.variants : [];
    if (!variants.length) return;

    const sliderControls = await setupSlider(section);
    setupServingToggle(section);

    const variantButtons = Array.from(section.querySelectorAll('[data-variant-button]'));
    let currentVariant =
      variants.find((variant) => Number(variant.id) === Number(config.currentVariantId)) ||
      variants[0];

    const selectVariant = (variantId) => {
      const nextVariant = variants.find((variant) => Number(variant.id) === Number(variantId));
      if (!nextVariant) return;
      currentVariant = nextVariant;
      updateVariantButtons(variantButtons, currentVariant.id);
      updatePurchaseUI(section, currentVariant, config, sliderControls);
    };

    variantButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        selectVariant(button.dataset.variantId);
      });
    });

    updateVariantButtons(variantButtons, currentVariant.id);
    updatePurchaseUI(section, currentVariant, config, sliderControls);

    section.dataset.productHeroInitialized = 'true';
    sliderRegistry.set(section, { sliderControls });
  };

  const initAll = () => {
    document
      .querySelectorAll('[data-product-hero]')
      .forEach((section) => initializeHero(section));
  };

  document.addEventListener('DOMContentLoaded', initAll);

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.('[data-product-hero]');
    if (section) {
      initializeHero(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target?.querySelector?.('[data-product-hero]');
    if (!section) return;

    const entry = sliderRegistry.get(section);
    entry?.sliderControls?.destroy?.();
    sliderRegistry.delete(section);
    delete section.dataset.productHeroInitialized;
  });
})();



